import {Injectable} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  OfferTextNewSettings,
  OfferTextSettingsPayload,
  OfferTextTaglineType,
  OfferTextUsedSettings,
  UseDefaultTagline
} from './offer-text.models';

const defaultTaglineMaxLength = 256;
const taglineTypeValidationPattern = '^(marquee|static)$';
const useDefaultTaglineValidationPattern = '^(1|0)$';
const sharedAccountFieldNames = [
  'dealerDiscountLabel',
  'taglineType'
];
const newOnlySharedFormControlNames = [
  'leaseDownDueAfterText',
  'alternateLeaseDownDueAfterText'
];

@Injectable({
  providedIn: 'root'
})
export class OfferTextFormService {
  private formBuilder: FormBuilder;

  formGroup: FormGroup;

  constructor(formBuilder: FormBuilder)
  {
    this.formBuilder = formBuilder;
    this.formGroup = this.formBuilder.group({
      new: this.buildNewFormGroup(),
      used: this.buildUsedFormGroup()
    });
    this.syncSharedAccountFields();
  }

  /**
   * Builds the settings payload submitted by the shared visible text form.
   */
  buildPayload(accountId: number): OfferTextSettingsPayload
  {
    const newSettings = this.formGroup.controls['new'].value as OfferTextNewSettings;
    const usedSettings = this.formGroup.controls['used'].value as OfferTextUsedSettings;

    this.normalizeDropdownFields(newSettings);
    this.normalizeDropdownFields(usedSettings);

    const payload = {
      accountId,
      new: newSettings,
      used: usedSettings
    };

    return payload;
  }

  /**
   * Populates the form from the settings endpoint and clears dirty state.
   */
  hydrate(settingsPayload: OfferTextSettingsPayload): void
  {
    this.formGroup.patchValue({
      new: settingsPayload.new,
      used: settingsPayload.used
    });
    this.formGroup.markAsPristine();
  }

  /**
   * Clears dirty state after a successful save.
   */
  markAsPristine(): void
  {
    this.formGroup.markAsPristine();
  }

  /**
   * Marks all controls as touched so validation messages can display.
   */
  markAllAsTouched(): void
  {
    this.formGroup.markAllAsTouched();
  }

  /**
   * Indicates whether all visible text controls are currently valid.
   */
  isValid(): boolean
  {
    const isValid = this.formGroup.valid;

    return isValid;
  }

  /**
   * Indicates whether the form has unsaved user changes.
   */
  isDirty(): boolean
  {
    const isDirty = this.formGroup.dirty;

    return isDirty;
  }

  /**
   * Builds the new-vehicle condition form group.
   */
  private buildNewFormGroup(): FormGroup
  {
    const newFormGroup = this.formBuilder.group({
      msrpTitle: [''],
      ...this.buildSharedFormControls()
    });

    return newFormGroup;
  }

  /**
   * Applies safe dropdown defaults before the payload is saved.
   */
  private normalizeDropdownFields(settings: OfferTextNewSettings | OfferTextUsedSettings): void
  {
    settings.taglineType = this.getTaglineTypeValue(settings.taglineType);
    settings.useDefaultTagline = this.getUseDefaultTaglineValue(settings.useDefaultTagline);
  }

  /**
   * Normalizes unknown tagline type values to a valid option.
   */
  private getTaglineTypeValue(taglineType: unknown): OfferTextTaglineType
  {
    let normalizedTaglineType: OfferTextTaglineType = 'marquee';

    if (taglineType === 'static') {
      normalizedTaglineType = 'static';
    }

    return normalizedTaglineType;
  }

  /**
   * Normalizes unknown default-tagline values to a valid option.
   */
  private getUseDefaultTaglineValue(useDefaultTagline: unknown): UseDefaultTagline
  {
    let normalizedUseDefaultTagline: UseDefaultTagline = '0';

    if (useDefaultTagline === '1') {
      normalizedUseDefaultTagline = '1';
    }

    return normalizedUseDefaultTagline;
  }

  /**
   * Builds the used-vehicle condition form group without new-only controls.
   */
  private buildUsedFormGroup(): FormGroup
  {
    const usedFormControls = this.buildSharedFormControls();
    this.removeNewOnlyFormControls(usedFormControls);
    const usedFormGroup = this.formBuilder.group({
      marketValueText: [''],
      ...usedFormControls
    });

    return usedFormGroup;
  }

  /**
   * Builds the form controls shared by new and used visible text settings.
   */
  private buildSharedFormControls(): Record<string, unknown>
  {
    const sharedFormControls = {
      buyTitle: [''],
      purchaseAfterText: [''],
      savingsTitle: [''],
      savingsAfterText1: [''],
      savingsAfterText2: [''],
      financePaymentAfterText: [''],
      showFinanceAprInOfferSet: [false],
      showFinanceDownPaymentInOfferSet: [false],
      aprPricingTitle: [''],
      aprRateAfterText: [''],
      aprTermAfterText: [''],
      leaseTitleText: [''],
      leasePaymentAfterText: [''],
      leaseDownDueAfterText: [''],
      leaseDownText: [''],
      alternateLeaseTitle: [''],
      alternateLeasePaymentAfterText: [''],
      alternateLeaseDownDueAfterText: [''],
      inventoryButtonText: [''],
      dealerDiscountLabel: [''],
      defaultTagline: ['', Validators.maxLength(defaultTaglineMaxLength)],
      taglineType: ['marquee', Validators.pattern(taglineTypeValidationPattern)],
      useDefaultTagline: ['0', Validators.pattern(useDefaultTaglineValidationPattern)],
      staticDisclosureLine: ['']
    };

    return sharedFormControls;
  }

  /**
   * Removes controls that do not have used-vehicle settings parity.
   */
  private removeNewOnlyFormControls(formControls: Record<string, unknown>): void
  {
    newOnlySharedFormControlNames.forEach((fieldName) => {
      delete formControls[fieldName];
    });
  }

  /**
   * Keeps account-level controls synchronized between condition tabs.
   */
  private syncSharedAccountFields(): void
  {
    sharedAccountFieldNames.forEach((fieldName) => {
      this.syncSharedAccountField(fieldName);
    });
  }

  /**
   * Mirrors one account-level control between new and used form groups.
   */
  private syncSharedAccountField(fieldName: string): void
  {
    const newControl = this.getConditionControl('new', fieldName);
    const usedControl = this.getConditionControl('used', fieldName);

    newControl.valueChanges.subscribe((fieldValue: unknown) => {
      usedControl.setValue(fieldValue, {emitEvent: false});
    });
    usedControl.valueChanges.subscribe((fieldValue: unknown) => {
      newControl.setValue(fieldValue, {emitEvent: false});
    });
  }

  /**
   * Looks up one control inside a condition-specific form group.
   */
  private getConditionControl(conditionName: string, fieldName: string): AbstractControl
  {
    const conditionFormGroup = this.formGroup.controls[conditionName] as FormGroup;
    const conditionControl = conditionFormGroup.controls[fieldName];

    return conditionControl;
  }
}
